/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export interface PetType {
    petID: string;
    SubscriberID: string;
    petName: string;
    petBirthday: string;
    petSpecies: string;
    petBreed: string;
    _CustomObjectKey: string;
    isActive: boolean;
    petProperties: PetPropertyModel[];
}

export interface PetsType {
    userPets: Array<PetType>;
}

export interface PetPropertyModel {
    ppid?: string;
    petId?: string;
    ppName?: string;
    ppValue?: string;
    _CustomObjectKey?: string;
}

export interface PetProduct {
    Id: string;
    PageTitle: string;
    Name: string;
    CategoryId: string;
    WebsiteId: string;
    CategoryName: string;
    WebsiteName: string;
}

export interface MiscellaneousModel {
    Id: string;
    ParentId: string;
    Name: string;
    Description: string;
}

export interface FilterValue {
    Id: string;
    Name: string;
    Value: string;
}

type MyPetsState = Readonly<{
    userPets: [];
    petNumberOfVists: MiscellaneousModel[];
    years: [];
    catBreeds: MiscellaneousModel[];
    dogBreeds: MiscellaneousModel[];
    catNeeds: MiscellaneousModel[];
    dogNeeds: MiscellaneousModel[];
    catProducts: PetProduct[];
    dogProducts: PetProduct[];
    dogSizes: FilterValue[];
    isLoading: boolean;
}>;

const initialMyPetsState: MyPetsState = {
    userPets: [] as any,
    petNumberOfVists: [] as any,
    years: [],
    catBreeds: [] as any,
    dogBreeds: [] as any,
    catNeeds: [] as any,
    dogNeeds: [] as any,
    catProducts: [] as any,
    dogProducts: [] as any,
    dogSizes: [] as any,
    isLoading: false,
};

const reducer = {
    "xMyPets/setIsLoading": (draft: Draft<MyPetsState>, action: { isLoading: boolean }) => {
        draft.isLoading = action.isLoading;
    },
    "xMyPets/setMyPets": (draft: Draft<MyPetsState>, action: { myPets: [] }) => {
        draft.userPets = action.myPets;
    },
    "xMyPets/setPetNumberOfVists": (draft: Draft<MyPetsState>, action: { petNumberOfVists: MiscellaneousModel[] }) => {
        draft.petNumberOfVists = action.petNumberOfVists;
    },
    "xMyPets/setPetBirthdayYears": (draft: Draft<MyPetsState>, action: { years: [] }) => {
        draft.years = action.years;
    },
    "xMyPets/setCatBreeds": (draft: Draft<MyPetsState>, action: { breeds: MiscellaneousModel[] }) => {
        draft.catBreeds = action.breeds;
    },
    "xMyPets/setDogBreeds": (draft: Draft<MyPetsState>, action: { breeds: MiscellaneousModel[] }) => {
        draft.dogBreeds = action.breeds;
    },
    "xMyPets/setCatNeeds": (draft: Draft<MyPetsState>, action: { needs: MiscellaneousModel[] }) => {
        draft.catNeeds = action.needs;
    },
    "xMyPets/setDogNeeds": (draft: Draft<MyPetsState>, action: { needs: MiscellaneousModel[] }) => {
        draft.dogNeeds = action.needs;
    },
    "xMyPets/setCatProducts": (draft: Draft<MyPetsState>, action: { products: PetProduct[] }) => {
        draft.catProducts = action.products;
    },
    "xMyPets/setDogProducts": (draft: Draft<MyPetsState>, action: { products: PetProduct[] }) => {
        draft.dogProducts = action.products;
    },
    "xMyPets/setDogSizes": (draft: Draft<MyPetsState>, action: { sizes: FilterValue[] }) => {
        draft.dogSizes = action.sizes;
    },
};

const xMyPets = createTypedReducerWithImmer(initialMyPetsState, reducer);

(reducers as any).xMyPets = xMyPets;

export type xMyPetsState = {
    xMyPets: MyPetsState;
};

type xMyPetsReducers = {
    userPets: typeof xMyPets;
    isLoading: typeof xMyPets;
    petNumberOfVists: typeof xMyPets;
    years: typeof xMyPets;
    catBreeds: typeof xMyPets;
    dogBreeds: typeof xMyPets;
    catProducts: typeof xMyPets;
    dogProducts: typeof xMyPets;
    catNeeds: typeof xMyPets;
    dogNeeds: typeof xMyPets;
    dogSizes: typeof xMyPets;
};

export type xMyPetsActions = Parameters<xMyPetsReducers[keyof xMyPetsReducers]>[1] | AnyAction;
